import { Collapse as AntdCollapse } from 'antd';
import { ComponentProps, memo, useEffect, useMemo, useState } from 'react';

type CollapseGroupItem = {
  id: number | string;
  label?: {
    text: string | React.ReactNode;
    extra?: string | React.ReactNode;
  };
  children: React.ReactNode;
};

export interface CollapseGroupProps {
  items: CollapseGroupItem[];
  open?: boolean;
  activeKeys?: (string | number)[]; // NEW: allow specific keys to be expanded
}

function CollapseGroupComponent({ items, open, activeKeys: propActiveKeys }: CollapseGroupProps) {
  const [activeKeys, setActiveKeys] = useState<CollapseGroupItem['id'][]>(() => {
    // If specific activeKeys are provided, use them
    if (propActiveKeys) {
      return propActiveKeys;
    }

    if (open) {
      return items.map((item) => item.id);
    }

    return [];
  });

  useEffect(() => {
    // If specific activeKeys are provided, use them and ignore open prop
    if (propActiveKeys) {
      setActiveKeys(propActiveKeys);
      return;
    }

    if (open === undefined) {
      return;
    }

    if (!open) {
      setActiveKeys([]);
      return;
    }

    setActiveKeys(items.map((item) => item.id));
  }, [open, items, propActiveKeys]);

  const antCollapseItems = useMemo<ComponentProps<typeof AntdCollapse>['items']>(() => {
    return items.map<NonNullable<ComponentProps<typeof AntdCollapse>['items']>[number]>((item) => ({
      key: item.id,
      label: item.label?.text,
      extra: item.label?.extra,
      children: item.children,
    }));
  }, [items]);

  return <AntdCollapse items={antCollapseItems} onChange={setActiveKeys} activeKey={activeKeys} />;
}

export const CollapseGroup = memo(CollapseGroupComponent);
